"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaLightbulb, FaRocket, FaUsers, FaCode, FaGlobe, FaMobile, FaCloud } from "react-icons/fa";
import HeroSection from "../../components/common/HeroSection";

const services = [
  {
    icon: <FaGlobe className="w-6 h-6" />,
    title: "Web Development",
    description: "Modern and responsive web applications"
  },
  {
    icon: <FaMobile className="w-6 h-6" />,
    title: "Mobile Solutions",
    description: "Native and cross-platform mobile apps"
  },
  {
    icon: <FaCloud className="w-6 h-6" />,
    title: "Cloud Services",
    description: "Scalable cloud infrastructure"
  },
  {
    icon: <FaUsers className="w-6 h-6" />,
    title: "Consulting Service",
    description: "Strategic technology advisory and solutions"
  }
];

const values = [
  {
    icon: <FaRocket className="w-6 h-6" />,
    title: "Innovation First",
    description: "Pushing boundaries with cutting-edge solutions"
  },
  {
    icon: <FaLightbulb className="w-6 h-6" />,
    title: "Creative Solutions",
    description: "Unique approaches to complex challenges"
  },
  {
    icon: <FaUsers className="w-6 h-6" />,
    title: "Client Focus",
    description: "Building lasting partnerships"
  },
  {
    icon: <FaCode className="w-6 h-6" />,
    title: "Quality Code",
    description: "Clean, efficient, and scalable solutions"
  }
];

// Add achievement stats
const stats = [
  { value: "50+", label: "Projects Delivered" },
  { value: "100%", label: "Client Satisfaction" },
  { value: "24/7", label: "Support" },
  { value: "15+", label: "Technologies" }
];

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      <HeroSection 
        title="Innovating the Future"
        description="A young tech company dedicated to transforming ideas into powerful digital solutions."
        showPattern={true}
      />
      
      {/* Vision & Mission with enhanced interactions */}
      <section className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 mb-12 sm:mb-16 md:mb-20">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ scale: 1.03, y: -5 }}
          className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Our Vision</h2>
          <p className="text-base sm:text-lg text-gray-600">
            To be at the forefront of technological innovation, creating solutions that empower businesses 
            and enhance digital experiences.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ scale: 1.03, y: -5 }}
          className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Our Mission</h2>
          <p className="text-base sm:text-lg text-gray-600">
            To deliver exceptional digital solutions through innovation, expertise, and 
            unwavering commitment to client success.
          </p>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-indigo-50 via-purple-50 to-white">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 md:mb-16"
          >
            Our Services
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                    text-white flex items-center justify-center mb-4 sm:mb-6"
                >
                  {service.icon}
                </motion.div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 md:mb-16"
          >
            Our Values
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                  text-white flex items-center justify-center mb-4 sm:mb-6"
                >
                  {value.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">{value.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.1 }}
                className="text-center p-4 sm:p-6 rounded-3xl bg-gradient-to-br from-indigo-50 to-purple-50"
              >
                <motion.div 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-indigo-600 mb-2"
                  initial={{ scale: 0.5 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-sm sm:text-base text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8"
          >
            Ready to Start Your Project?
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link
              to="/contact"
              className="group inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-white text-indigo-600 
                hover:shadow-lg hover:scale-105 transition-all duration-300 text-base sm:text-lg font-semibold"
            >
              Get in Touch
              <motion.svg 
                className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1"
                animate={{ x: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </motion.svg>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}